/**
 * --- KONSEP: PENYIMPANAN GAMBAR (CLOUDINARY) ---
 * File ini bertugas untuk mengirim foto produk dari komputer/HP
 * ke internet (Cloudinary) agar bisa dilihat oleh semua orang.
 */

// Membersihkan data dari file .env agar tidak ada spasi atau tanda kutip yang salah
const sanitize = (val) => val?.replace(/['"'\u201c\u201d\s]+/g, "").trim();

const cloudName = sanitize(process.env.REACT_APP_CLOUDINARY_CLOUD_NAME);
const apiKey = sanitize(process.env.REACT_APP_CLOUDINARY_API_KEY);
const apiSecret = sanitize(process.env.REACT_APP_CLOUDINARY_API_SECRET);
const uploadPreset = sanitize(process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);

/**
 * LOGIKA: Membuat Kode Keamanan (SHA-1)
 * Digunakan agar Cloudinary tahu bahwa permintaan upload ini resmi dari aplikasi kita.
 */
async function sha1(message) {
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  const hashBuffer = await crypto.subtle.digest("SHA-1", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * --- UTAMA: Fungsi Upload Gambar ---
 * @param {File} file - File foto yang dipilih
 * @param {string} [existingPublicId=null] - (Opsional) ID foto lama jika ingin menindih/mengganti foto
 */
export const uploadImageToCloudinary = async (
  file,
  existingPublicId = null,
) => {
  if (!cloudName) {
    throw new Error("Kunci Cloudinary tidak ditemukan di .env");
  }

  // Menyiapkan paket data untuk dikirim
  const formData = new FormData();
  formData.append("file", file);

  // Jika menggunakan kunci rahasia (API Key & Secret)
  if (apiKey && apiSecret) {
    const timestamp = Math.round(Date.now() / 1000);
    const params = existingPublicId
      ? `public_id=${existingPublicId}&timestamp=${timestamp}`
      : `timestamp=${timestamp}`;

    const signature = await sha1(`${params}${apiSecret}`);

    formData.append("api_key", apiKey);
    formData.append("timestamp", timestamp);
    formData.append("signature", signature);
    if (existingPublicId) formData.append("public_id", existingPublicId);
  } else if (uploadPreset) {
    // Jika menggunakan cara yang lebih sederhana (Upload Preset)
    formData.append("upload_preset", uploadPreset);
  } else {
    throw new Error("Tidak ada kredensial Cloudinary yang ditemukan di .env");
  }

  // Proses pengiriman data ke server Cloudinary
  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
    { method: "POST", body: formData },
  );

  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(
      errorData.error?.message || "Gagal mengunggah foto ke Cloudinary.",
    );
  }

  const data = await res.json();
  // Mengembalikan URL foto yang sudah jadi di internet
  return { url: data.secure_url, public_id: data.public_id };
};
